import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import EmptySection from './EmptySection';
import MuiDialog from '../../../UI/MuiDialog/MuiDialog';
import PregledSlikaPopupUniverzalno from '../../../UI/PregledSlika/PregledSlikaPopupUniverzalno';
import Spinner from '../../../UI/Modal/Spinner';
import useHttp from '../../../../hooks/use-http';
import styles from './UpdateDetail.module.css';

const UpdateDetail = ({ update, hasData, onClose }) => {
  const { t } = useTranslation();
  const { getData, isLoading } = useHttp('');
  const [selectedImg, setSelectedImg] = useState('');
  const [openDocument, setOpenDocument] = useState(false);
  const [openPath, setOpenPath] = useState(null);

  const handleView = () => {
    if (!update?.path) return;

    if (update.isImage || update.type?.startsWith('image')) {
      setSelectedImg(update.path);
      return;
    }

    setOpenPath(update.path);
    setOpenDocument(true);
  };

  const handleDownload = async () => {
    if (!update?.path) return;

    const response = await getData({
      method: 'POST',
      url: 'Dokumenta/konvertovanje_base64',
      data: { vrednost: update.path },
    });

    if (!response || response?.isAxiosError) return;

    const base64Data = response;
    const byteCharacters = atob(base64Data);
    const byteNumbers = Array.from(byteCharacters, (char) =>
      char.charCodeAt(0),
    );
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/octet-stream' });
    const blobUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', update.name || update.fajl?.naziv || '');
    document.body.appendChild(link);
    link.click();

    URL.revokeObjectURL(blobUrl);
    document.body.removeChild(link);
  };

  const fileIconClass = update?.isImage
    ? 'fas fa-file-image'
    : update?.isVideo
    ? 'fas fa-file-video'
    : 'fas fa-file';

  const renderTitle = (naslov) => {
    if (!naslov) return null;
    const separatorIndex = naslov.indexOf('/');
    if (separatorIndex <= 0) return naslov;

    return (
      <>
        <span className={styles.titleCode}>
          {naslov.slice(0, separatorIndex)}
        </span>
        <span className={styles.titleRest}>{naslov.slice(separatorIndex)}</span>
      </>
    );
  };

  return (
    <div className={styles.card}>
      {isLoading && <Spinner />}
      <div className={styles.sectionTitleRow}>
        <div className={styles.sectionTitleLeft}>
          <h3 className={styles.sectionTitle}>Update Detail</h3>
          {update ? (
            <span className={styles.meta}>{update.vreme || update.datum}</span>
          ) : null}
        </div>
        {update?.kategorija ? (
          <span className={styles.categoryTag}>{update.kategorija}</span>
        ) : null}
      </div>
      {!hasData ? (
        <div className={styles.cardBody}>
          <EmptySection />
        </div>
      ) : !update ? (
        <div className={styles.cardBody}>
          <EmptySection message={t('projectProgress.selectUpdate')} />
        </div>
      ) : (
        <div className={styles.cardBody}>
          <div className={styles.scrollContent}>
            <h3 className={styles.title}>{renderTitle(update.naslov)}</h3>
            {update.opis ? (
              <p className={styles.description}>{update.opis}</p>
            ) : null}

            {update.isImage && update.path ? (
              <div className={styles.gallery}>
                <button
                  type="button"
                  className={styles.galleryImageWrap}
                  onClick={handleView}
                  title="Open file"
                >
                  <img
                    src={update.path}
                    alt={update.name || update.naslov}
                    className={styles.galleryImage}
                  />
                </button>
              </div>
            ) : null}

            {update.fajl?.naziv ? (
              <div className={styles.filesSection}>
                <div className={styles.fileItem}>
                  <i className={`${fileIconClass} ${styles.fileIcon}`} />
                  <div className={styles.fileInfo}>
                    <div className={styles.fileName}>{update.fajl.naziv}</div>
                    {update.fajl.velicina ? (
                      <div className={styles.fileSize}>
                        {update.fajl.velicina}
                      </div>
                    ) : null}
                  </div>
                  <div className={styles.fileActions}>
                    <button
                      type="button"
                      className={styles.actionBtn}
                      onClick={handleView}
                      title="Open file"
                    >
                      <i className="fas fa-eye" />
                    </button>
                    <button
                      type="button"
                      className={styles.actionBtn}
                      onClick={handleDownload}
                      title="Download file"
                    >
                      <i className="fas fa-download" />
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          <div className={styles.footer}>
            <button type="button" className={styles.closeBtn} onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      )}

      {selectedImg && (
        <PregledSlikaPopupUniverzalno
          selectedImg={selectedImg}
          setSelectedImg={setSelectedImg}
        />
      )}

      <MuiDialog
        maxWidth="xl"
        open={openDocument}
        title="Document"
        content={
          <iframe
            className={styles.fileContainer}
            src={openPath}
            title="File"
          />
        }
        handleClose={() => setOpenDocument(false)}
        deniedText="Close"
      />
    </div>
  );
};

export default UpdateDetail;
